import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  List,
  Tag,
  Button,
  Alert,
  Modal,
} from 'antd'
import {
  DollarOutlined,
  AppstoreOutlined,
  LineChartOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { fetchDashboardData } from '../../store/slices/dashboardSlice'
import type { RootState, AppDispatch } from '../../store'
import { useDataRefreshInterval } from '../../hooks/useDataRefreshInterval'
import StatCard from '../../components/StatCard'
import QuickActions from '../../components/QuickActions'
import RecentActivities from '../../components/RecentActivities'

const ACTIVITY_TYPE_LABEL: Record<string, string> = {
  asset_added: '新增',
  asset_updated: '更新',
  data_imported: '导入',
  report_generated: '报告',
}

const Dashboard = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { user, hasPermission } = useAuth()
  const canAccessData = hasPermission('data_sources:list') || hasPermission('data_sources:summary')
  const canAccessAssets = hasPermission('asset_products:list') || hasPermission('asset_products:detail')
  const canAccessAnalytics = hasPermission('analytics:indicators') || hasPermission('analytics:reports_list')
  const canAccessSettings = hasPermission('settings:profile_get') || hasPermission('settings:system_get')
  const { loading, error, stats, recentActivities, quickActions, distribution } = useSelector(
    (state: RootState) => state.dashboard
  )
  const distributionColors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444']
  const [activityModalVisible, setActivityModalVisible] = useState(false)
  const refreshIntervalMs = useDataRefreshInterval()

  const greeting = (() => {
    const hour = dayjs().hour()
    if (hour < 12) return '早上好'
    if (hour < 18) return '下午好'
    return '晚上好'
  })()
  const displayName = user?.nickname ?? user?.username ?? '用户'

  useEffect(() => {
    dispatch(fetchDashboardData())
  }, [dispatch])

  // 根据系统设置「数据刷新频率」轮询仪表盘数据
  useEffect(() => {
    if (refreshIntervalMs == null || refreshIntervalMs <= 0) return
    const timer = setInterval(() => {
      dispatch(fetchDashboardData())
    }, refreshIntervalMs)
    return () => clearInterval(timer)
  }, [dispatch, refreshIntervalMs])

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => dispatch(fetchDashboardData())}>
            重试
          </Button>
        }
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* 欢迎语和概览 */}
      <div>
        <h1 className="text-2xl font-bold mb-2">{greeting}，{displayName}！</h1>
        <p className="text-gray-600">这是您今天的资产数据概览</p>
      </div>

      {/* 关键指标卡片 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="总资产价值"
            value={`¥${(stats.totalValue / 10000).toFixed(2)}万`}
            icon={<DollarOutlined />}
            color="blue"
            trend={{
              value: stats.dailyGrowth,
              isPositive: stats.dailyGrowth >= 0,
            }}
            loading={loading}
            onClick={canAccessData ? () => navigate('/data') : undefined}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="资产数量"
            value={stats.totalAssets.toString()}
            icon={<AppstoreOutlined />}
            color="green"
            trend={{
              value: stats.dailyGrowthCount,
              isPositive: stats.dailyGrowthCount >= 0,
            }}
            loading={loading}
            onClick={canAccessAssets ? () => navigate('/assets') : undefined}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="月增长率"
            value={`${stats.monthlyGrowth.toFixed(1)}%`}
            icon={<LineChartOutlined />}
            color="purple"
            trend={{
              value: stats.monthlyGrowth,
              isPositive: stats.monthlyGrowth >= 0,
            }}
            loading={loading}
            onClick={canAccessAnalytics ? () => navigate('/analytics') : undefined}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="活跃产品"
            value={stats.activeProducts.toString()}
            icon={<CheckCircleOutlined />}
            color="orange"
            suffix={`/ ${stats.totalAssets}`}
            loading={loading}
            onClick={canAccessAssets ? () => navigate('/assets') : undefined}
          />
        </Col>
      </Row>

      {/* 资产分布和快速操作 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="资产分布概览" loading={loading}>
            <div className="space-y-4">
              {(distribution?.length ? distribution : []).map((item, index) => (
                <div key={item.label}>
                  <div className="flex justify-between mb-2">
                    <span>{item.label}</span>
                    {/* <span className="font-medium">{item.percent}%</span> */}
                  </div>
                  <Progress
                    percent={item.percent}
                    strokeColor={distributionColors[index % distributionColors.length]}
                  />
                </div>
              ))}
            </div>
          </Card>
        </Col>
        
        <Col xs={24} lg={8}>
          <QuickActions
            actions={quickActions}
            loading={loading}
            canAccessSettings={canAccessSettings}
            hasPermission={hasPermission}
          />
        </Col>
      </Row>

      {/* 最近活动 */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <RecentActivities
            activities={recentActivities}
            loading={loading}
            onViewAll={() => setActivityModalVisible(true)}
          />
        </Col>
      </Row>

      {/* 全部活动弹窗 */}
      <Modal
        title="全部活动"
        open={activityModalVisible}
        onCancel={() => setActivityModalVisible(false)}
        footer={<Button type="primary" onClick={() => setActivityModalVisible(false)}>关闭</Button>}
        width={560}
      >
        <List
          dataSource={recentActivities}
          renderItem={(activity) => (
            <List.Item>
              <List.Item.Meta
                title={
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{activity.title}</span>
                    <Tag color="blue">{ACTIVITY_TYPE_LABEL[activity.type] ?? activity.type}</Tag>
                  </div>
                }
                description={
                  <div>
                    <div className="text-gray-600">{activity.description}</div>
                    <div className="flex justify-between mt-1 text-sm text-gray-500">
                      <span>{activity.user}</span>
                      <span>{dayjs(activity.timestamp).format('YYYY-MM-DD HH:mm')}</span>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
        {recentActivities.length === 0 && (
          <div className="text-center py-8 text-gray-500">暂无活动记录</div>
        )}
      </Modal>

      {/* 性能指标 */}
      <Card title="系统性能指标" loading={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Statistic
              title="数据加载速度"
              value={98.5}
              suffix="%"
              valueStyle={{ color: '#10b981' }}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="API响应时间"
              value={124}
              suffix="ms"
              valueStyle={{ color: '#3b82f6' }}
            />
          </Col>
        </Row>
      </Card>
    </div>
  )
}

export default Dashboard